<template>
  <div class="InsurancePersonalDataStep5Form">
    <h2 class="gl_form-title small">
      {{ t('hiring.common.insurance-personal-data-step-6.product-you-are-contracting') }}
    </h2>

    <template v-if="extra?.showByLanguage">
      <p class="hiring-subtitle">
        {{ t('hiring.common.insurance-personal-data-step-6.by-lang-title') }}
      </p>

      <div>
        <RadioButtonGroupComponent
          id="language"
          :options="langOptions"
          :value="currentLanguage"
          v-model="currentLanguage"
          flexDirection="row"
          :scroll="true"
        />
      </div>
    </template>

    <template v-if="loaded">
      <template v-if="documents?.length">
        <section class="download-buttons">
          <BaseButton
            v-for="(document, i) in documents"
            :key="i"
            icon="icon-file-02"
            type="transparent"
            @click="download(document.downloadLink, document.name)"
          >
            {{ document.name }}
          </BaseButton>
        </section>
      </template>
      <div
        v-else-if="extra?.showByLanguage"
        class="no-lang-docs_wrapper"
      >
        <i class="icon-incorrect" />
        <div class="no-lang-docs_text-wrapper">
          <template v-if="!loadingError[currentLanguage]">
            <h3>{{ t('hiring.common.insurance-personal-data-step-6.by-lang-no-docs') }}</h3>
            <p>{{ t('hiring.common.insurance-personal-data-step-6.by-lang-change-choice') }}</p>
          </template>
          <template v-else-if="!retries[currentLanguage]">
            <h3>
              {{ t('hiring.common.insurance-personal-data-step-6.by-lang-load-first-error-title') }}
            </h3>
            <p>
              {{ t('hiring.common.insurance-personal-data-step-6.by-lang-load-first-error-text') }}
            </p>
            <div class="lang-retry-btn_wrapper">
              <BaseButton
                type="outlined"
                @click="getDocuments(true)"
              >
                {{ t('hiring.common.insurance-personal-data-step-6.retry-btn') }}
              </BaseButton>
            </div>
          </template>
          <template v-else>
            <h3>
              {{
                t('hiring.common.insurance-personal-data-step-6.by-lang-load-second-error-title')
              }}
            </h3>
            <p>
              {{ t('hiring.common.insurance-personal-data-step-6.by-lang-load-second-error-text') }}
            </p>
            <div class="lang-help-btn_wrapper">
              <BaseButton
                v-if="businessLine"
                class="help-button"
                type="outlined"
                icon="icon-message-text-01"
                :name="'support-modal-btn'"
                :id="'support-modal-btn'"
                @click="openSupportModal"
              >
                {{ t('header.help-text') }}
              </BaseButton>
            </div>
          </template>
        </div>
      </div>
      <section
        v-else
        class="download-buttons"
      >
        <div class="retry-wrapper">
          <p>{{ t('hiring.common.insurance-personal-data-step-6.retry-msg') }}</p>
          <BaseButton
            class="retry-btn"
            icon="icon-arrow-refresh-01"
            type="transparent"
            @click="getDocuments"
          >
            {{ t('hiring.common.insurance-personal-data-step-6.retry-btn') }}
          </BaseButton>
        </div>
      </section>
    </template>

    <BaseCheckBox
      v-if="documents.length || (loaded && extra?.showByLanguage)"
      id="document-checked"
      v-model="preContractualDocumentChecked"
      :label="
        t('hiring.common.insurance-personal-data-step-6.i-have-read-and-accept-precontractual-docs')
      "
      :rules="[validationRules.checkRequired]"
      :disabled="!documents?.length"
    />
  </div>
</template>

<script lang="ts" setup>
  import { computed, onBeforeMount, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import BaseCheckBox from '@/modules/core/components/ui/BaseCheckBox.vue'
  import Insured from '@/modules/common/common/models/APP/Insured'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import SpanishBranches from '@/modules/core/models/SpanishBranches'
  import { useBusinessStore } from '@/modules/core/stores/businessStore'
  import RadioButtonGroupComponent from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import { useToastStore } from '@/modules/core/stores/toastStore'
  import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
  import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'

  const { t } = useI18n()
  const commonStore = useCommonStore()
  const loadingStore = useLoadingStore()
  const { required } = useRules()
  const gtmStore = useGTMStore()
  const businessStore = useBusinessStore()
  const toastStore = useToastStore()
  const branchName = ref<keyof typeof SpanishBranches | null>(null)
  const gmtStore = useGTMStore()
  const sharedGtmDataStore = useSharedGtmDataStore()
  const supportModalStore = useSupportModalStore()

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    extra?: Record<string, any>
  }
  const props: PropsInterface = withDefaults(defineProps<PropsInterface>(), {})

  const validationRules = {
    checkRequired: (value: string) => required(value, 'common.validations.checked')
  }

  const langOptions = [
    {
      label: t('hiring.common.insurance-personal-data-step-6.by-lang-spanish'),
      value: 'Español'
    },
    {
      label: t('hiring.common.insurance-personal-data-step-6.by-lang-english'),
      value: 'Inglés'
    }
  ]

  const currentLanguage = ref<string>(langOptions[0].value)

  const documents = ref<{ name: string; downloadLink: string; docType: string }[]>([])
  const currentInsured = new Insured(
    props.extra?.customInsuredProp
      ? props.answers[props.extra.customInsuredProp]
      : props.answers.currentInsured
  )
  const preContractualDocumentChecked = ref(currentInsured.preContractualDocumentChecked)
  const loaded = ref(false)
  const files: Record<string, Array<any>> = {}
  const loadingError = ref<Record<string, boolean>>({})
  const retries = ref<Record<string, boolean>>({})
  const businessLine = computed(() => businessStore.getBusinessLine())

  async function download(link: string, name: string) {
    const [, id] = link.match(/id=([\w\d-]*)/) ?? [null, null]

    if (!id) return

    await commonStore.downloadDocument(id, name)

    branchName.value = props.configurations.businessLine

    gtmStore.sendEvent({
      event: GTMEventType.DOWNLOAD,
      producto: businessStore.getInsurance()?.productName ?? undefined,
      ramo: branchName.value ? SpanishBranches[branchName.value] : undefined,
      nombre_descarga: name
    })
  }

  function triggerSubmit() {
    const page = props.nextForm[0]

    currentInsured.preContractualDocumentChecked = preContractualDocumentChecked.value

    const answers = props.extra?.customInsuredProp
      ? { [props.extra.customInsuredProp]: currentInsured }
      : props.extra?.currentInsuredIndex === 'holder'
      ? { holder: currentInsured }
      : { preContractualDocumentChecked: currentInsured.preContractualDocumentChecked }

    return { page, answers }
  }

  async function getDocuments(retried = false) {
    if (files[currentLanguage.value]) {
      documents.value = files[currentLanguage.value]
      return
    }
    loadingStore.startLoading()

    try {
      documents.value = await commonStore.getDocuments(
        props.answers.product.productCode,
        props.answers.businessLine,
        props.answers.product.clientCode,
        currentLanguage.value ?? undefined
      )

      if (documents.value?.length) {
        files[currentLanguage.value] = documents.value
      }
    } catch (error) {
      if (!retries.value[currentLanguage.value]) {
        toastStore.errorToast(t('common.error.title'))
      }
      loadingError.value[currentLanguage.value] = true
    } finally {
      if (retried) {
        retries.value[currentLanguage.value] = true
      }
      loaded.value = true
      loadingStore.stopLoading()
    }
  }

  function openSupportModal() {
    gmtStore.sendEvent({
      event: GTMEventType.CALLMEBACK_BTN,
      click_name: 'support-modal-btn',
      click_id: 'support-modal-btn',
      btn_label: t('header.help-text'),
      value: sharedGtmDataStore.policyValue
    })
    supportModalStore.setOpenModal(true)
  }

  onBeforeMount(async () => {
    getDocuments()
    watch(currentLanguage, () => getDocuments())
  })
</script>

<style lang="scss" scoped>
  .InsurancePersonalDataStep5Form {
    .hiring-subtitle {
      margin: $spacing-xl 0;
      color: $color-neutral;
      font-size: $font-size-l;
      font-weight: $font-weight-400;
      line-height: $line-height-140p;
    }

    .lang-retry-btn_wrapper {
      width: $size-141;
      margin: 0 auto;

      .BaseButton {
        height: $size-48;
      }
    }

    .lang-help-btn_wrapper {
      width: $size-170;
      margin: 0 auto;

      .BaseButton {
        height: $size-48;
      }
    }

    .download-buttons {
      border-top: solid 1px $color-neutral-light;
      border-bottom: solid 1px $color-neutral-light;
      padding: $spacing-l 0;
      margin: $spacing-xl 0;

      .BaseButton:not(.retry-btn) {
        justify-content: start;
        text-align: left;
        word-break: break-all;
      }
    }

    .checkbox-subtitle {
      color: $color-neutral;
      margin-left: $spacing-26;
    }

    hr.v-divider.v-theme--asisaTheme {
      margin-top: 5%;
      margin-bottom: 5%;
    }

    .retry-wrapper {
      color: $color-neutral;
      text-align: center;

      p {
        margin-bottom: $spacing-s;
      }
    }

    ::v-deep(.RadioButtonGroupComponent .options.direction--row.scroll .card) {
      padding: $spacing-xl;
      border: 2px solid $color-neutral-light;

      &.selected {
        border: 2px solid $color-primary;
      }
    }

    .no-lang-docs_wrapper {
      background-color: $color-primary-lighter;
      text-align: center;
      padding: $spacing-36 0;
      margin-bottom: $spacing-xl;

      .icon-incorrect {
        font-size: $font-size-3xl;
        color: $color-error;
      }

      .no-lang-docs_text-wrapper {
        width: 90%;
        margin: 0 auto;

        h3 {
          font-size: $font-size-l;
          margin-top: $spacing-xl;
          margin-bottom: $spacing-s;
          font-weight: $font-weight-700;
          color: $color-primary-dark;
        }

        p {
          font-size: $font-size-l;
          font-weight: $font-weight-400;
          color: $color-primary-dark;
          margin-bottom: $spacing-s;
        }
      }
    }
  }
</style>
